/**
 * JWT Token utility functions
 */

interface DecodedToken {
  exp: number // Expiration time (Unix timestamp in seconds)
  iat: number // Issued at time
  sub: string // Subject (user ID)
  email?: string
  phone_number?: string
  [key: string]: unknown
}

/**
 * Decode JWT token without verification
 * Note: This is client-side decoding for reading claims only
 * Server-side verification is still required for security
 */
export function decodeToken(token: string): DecodedToken | null {
  try {
    const base64Url = token.split('.')[1]
    if (!base64Url) return null

    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    )

    return JSON.parse(jsonPayload) as DecodedToken
  } catch (error) {
    console.error('Error decoding token:', error)
    return null
  }
}

/**
 * Check if token is expired
 * @param token - JWT token string
 * @returns true if expired, false otherwise
 */
export function isTokenExpired(token: string | null): boolean {
  if (!token) return true

  const decoded = decodeToken(token)
  if (!decoded || !decoded.exp) return true

  // Get current time in seconds
  const currentTime = Math.floor(Date.now() / 1000)

  // Token is expired if exp is less than current time
  return decoded.exp < currentTime
}

/**
 * Check if token will expire soon (within specified minutes)
 * @param token - JWT token string
 * @param minutesBeforeExpiry - Number of minutes before expiry to consider as "soon" (default: 5)
 * @returns true if token will expire soon, false otherwise
 */
export function isTokenExpiringSoon(token: string | null, minutesBeforeExpiry: number = 5): boolean {
  if (!token) return true

  const decoded = decodeToken(token)
  if (!decoded || !decoded.exp) return true

  // Get current time in seconds
  const currentTime = Math.floor(Date.now() / 1000)

  // Calculate threshold time (current time + minutes before expiry)
  const thresholdTime = currentTime + minutesBeforeExpiry * 60

  // Token is expiring soon if exp is less than threshold time
  return decoded.exp < thresholdTime
}

/**
 * Get time remaining until token expiration
 * @param token - JWT token string
 * @returns Number of seconds until expiration, or 0 if expired/invalid
 */
export function getTokenTimeRemaining(token: string | null): number {
  if (!token) return 0

  const decoded = decodeToken(token)
  if (!decoded || !decoded.exp) return 0

  const currentTime = Math.floor(Date.now() / 1000)
  const remaining = decoded.exp - currentTime

  return remaining > 0 ? remaining : 0
}

/**
 * Get token expiration date
 * @param token - JWT token string
 * @returns Date object of expiration time, or null if invalid
 */
export function getTokenExpirationDate(token: string | null): Date | null {
  if (!token) return null

  const decoded = decodeToken(token)
  if (!decoded || !decoded.exp) return null

  return new Date(decoded.exp * 1000)
}

/**
 * Extract user info from token
 * @param token - JWT token string
 * @returns User info object or null if invalid
 */
export function getUserFromToken(token: string | null): {
  id: string
  email?: string
  phoneNumber?: string
} | null {
  if (!token) return null

  const decoded = decodeToken(token)
  if (!decoded) return null

  return {
    id: decoded.sub,
    email: decoded.email as string | undefined,
    phoneNumber: decoded.phone_number as string | undefined,
  }
}
